/**
 * Dashboard LaLiga: navegación por jornadas, modal de calendario y clasificación completa.
 */
(function () {
  const KNOCKOUT_ROUND_ORDER = [
    'round-of-32',
    'round-of-16',
    '8th-finals',
    'quarter-finals',
    'semi-finals',
    '3rd-place-final',
    'third-place-final',
    'final',
  ];

  /**
   * @param {HTMLElement} section
   * @returns {object | null}
   */
  function readData(section) {
    const jsonEl = section.querySelector('[data-offsider-laliga-json]');
    if (!jsonEl?.textContent?.trim()) return null;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {object} fixture
   * @returns {string}
   */
  function getFixtureRoundKey(fixture) {
    if (fixture.roundKey) return String(fixture.roundKey);
    if (fixture.round) return String(fixture.round);
    return '';
  }

  /**
   * @param {object} data
   * @returns {string[]}
   */
  function getSortedRoundKeys(data) {
    const keys = [];
    const seen = new Set();

    for (const fixture of data.fixtures) {
      const key = getFixtureRoundKey(fixture);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }

    return keys.sort((keyA, keyB) => compareRoundKeys(keyA, keyB, data.fixtures));
  }

  /**
   * @param {string} keyA
   * @param {string} keyB
   * @param {object[]} fixtures
   * @returns {number}
   */
  function compareRoundKeys(keyA, keyB, fixtures) {
    const fixtureA = fixtures.find((fixture) => getFixtureRoundKey(fixture) === keyA);
    const fixtureB = fixtures.find((fixture) => getFixtureRoundKey(fixture) === keyB);
    const knockoutA = Boolean(fixtureA?.isKnockout);
    const knockoutB = Boolean(fixtureB?.isKnockout);

    if (knockoutA !== knockoutB) {
      return knockoutA ? 1 : -1;
    }

    if (!knockoutA) {
      return Number(fixtureA?.round ?? 0) - Number(fixtureB?.round ?? 0);
    }

    const indexA = KNOCKOUT_ROUND_ORDER.indexOf(keyA);
    const indexB = KNOCKOUT_ROUND_ORDER.indexOf(keyB);

    if (indexA >= 0 && indexB >= 0) return indexA - indexB;
    if (indexA >= 0) return -1;
    if (indexB >= 0) return 1;

    return keyA.localeCompare(keyB);
  }

  /**
   * @param {object[]} fixtures
   * @param {string} roundKey
   * @returns {object | undefined}
   */
  function findFixtureForRound(fixtures, roundKey) {
    return fixtures.find((fixture) => getFixtureRoundKey(fixture) === roundKey);
  }

  /**
   * @param {object} data
   * @param {string} roundKey
   * @returns {string}
   */
  function formatRoundLabel(data, roundKey) {
    const fixture = findFixtureForRound(data.fixtures, roundKey);
    if (!fixture) return roundKey;

    if (fixture.isKnockout) {
      return fixture.roundLabel || roundKey;
    }

    const roundNumber = fixture.round || roundKey;
    return `${data.roundLabelPrefix || 'Jornada de'} ${roundNumber} de ${data.totalRounds}`;
  }

  class LaligaMatchesController {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.section = root.closest('.offsider-laliga-dashboard');
      this.data = this.section ? readData(this.section) : null;
      this.grid = root.querySelector('[data-offsider-laliga-matches-grid]');
      this.label = root.querySelector('[data-offsider-laliga-jornada-label]');
      this.prevBtn = root.querySelector('[data-offsider-laliga-jornada-prev]');
      this.nextBtn = root.querySelector('[data-offsider-laliga-jornada-next]');
      this.template = this.section?.querySelector('[data-offsider-laliga-match-template]');
      this.rounds = this.data ? getSortedRoundKeys(this.data) : [];
      this.currentRoundKey =
        this.data?.defaultRoundKey ||
        (this.data?.defaultRound ? String(this.data.defaultRound) : '') ||
        this.rounds[this.rounds.length - 1] ||
        '';
      this.calendarBuilt = false;
      this.roundLabelPrefix =
        this.data?.roundLabelPrefix ||
        this.section?.dataset.roundLabelPrefix ||
        'Jornada de';
      this.calendarRoundPrefix =
        this.section?.dataset.calendarRoundPrefix || 'Jornada';
    }

    init() {
      if (!this.data || !this.grid || !this.template) return;

      this.prevBtn?.addEventListener('click', () => this.#changeRound(-1));
      this.nextBtn?.addEventListener('click', () => this.#changeRound(1));
      this.renderCardMatches();
    }

    /** @param {number} delta */
    #changeRound(delta) {
      const index = this.rounds.indexOf(this.currentRoundKey);
      if (index < 0) return;

      const nextIndex = index + delta;
      if (nextIndex < 0 || nextIndex >= this.rounds.length) return;

      this.currentRoundKey = this.rounds[nextIndex];
      this.renderCardMatches();
    }

    renderCardMatches() {
      if (!this.data || !this.grid) return;

      const matches = this.data.fixtures
        .filter((fixture) => getFixtureRoundKey(fixture) === this.currentRoundKey)
        .slice(0, this.data.maxMatches);

      this.grid.replaceChildren();

      for (const match of matches) {
        this.grid.appendChild(this.#createMatchCard(match));
      }

      if (this.label) {
        this.label.textContent = formatRoundLabel(this.data, this.currentRoundKey);
      }

      if (this.prevBtn) {
        this.prevBtn.disabled = this.rounds.indexOf(this.currentRoundKey) <= 0;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = this.rounds.indexOf(this.currentRoundKey) >= this.rounds.length - 1;
      }
    }

    /** @param {object} match */
    #createMatchCard(match) {
      const fragment = this.template.content.cloneNode(true);
      const card = fragment.querySelector('[data-match-card]');
      if (!(card instanceof HTMLElement)) return document.createElement('div');

      const homeRow = card.querySelector('[data-home-row]');
      const awayRow = card.querySelector('[data-away-row]');
      const homeLogo = card.querySelector('[data-home-logo]');
      const awayLogo = card.querySelector('[data-away-logo]');

      const homeName = card.querySelector('[data-home-name]');
      const awayName = card.querySelector('[data-away-name]');
      const homeScore = card.querySelector('[data-home-score]');
      const awayScore = card.querySelector('[data-away-score]');
      const statusMain = card.querySelector('[data-status-main]');
      const statusSub = card.querySelector('[data-status-sub]');

      if (homeName) homeName.textContent = match.homeTeam;
      if (awayName) awayName.textContent = match.awayTeam;
      if (homeScore) homeScore.textContent = String(match.homeScore);
      if (awayScore) awayScore.textContent = String(match.awayScore);
      if (statusMain) statusMain.textContent = match.statusMain;
      if (statusSub) statusSub.textContent = match.statusSub;

      if (homeName) homeName.classList.toggle('offsider-laliga-match__name--muted', Boolean(match.homeMuted));
      if (awayName) awayName.classList.toggle('offsider-laliga-match__name--muted', Boolean(match.awayMuted));
      if (homeScore) homeScore.classList.toggle('offsider-laliga-match__score--muted', Boolean(match.homeMuted));
      if (awayScore) awayScore.classList.toggle('offsider-laliga-match__score--muted', Boolean(match.awayMuted));

      if (homeLogo instanceof HTMLImageElement) {
        if (match.homeLogo) {
          homeLogo.src = match.homeLogo;
          homeLogo.hidden = false;
        } else {
          homeLogo.hidden = true;
        }
      }

      if (awayLogo instanceof HTMLImageElement) {
        if (match.awayLogo) {
          awayLogo.src = match.awayLogo;
          awayLogo.hidden = false;
        } else {
          awayLogo.hidden = true;
        }
      }

      card.classList.toggle('offsider-laliga-match--knockout', Boolean(match.isKnockout));

      return card;
    }

    buildCalendarModal() {
      const body = this.section?.querySelector('[data-offsider-laliga-calendar-body]');
      if (!body || !this.data || this.calendarBuilt) return;

      const fragment = document.createDocumentFragment();

      for (const roundKey of [...this.rounds]) {
        const roundMatches = this.data.fixtures
          .filter((fixture) => getFixtureRoundKey(fixture) === roundKey)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const section = document.createElement('section');
        section.className = 'offsider-laliga-calendar-modal__round';
        const roundFixture = findFixtureForRound(this.data.fixtures, roundKey);
        if (roundFixture?.isKnockout) {
          section.classList.add('offsider-laliga-calendar-modal__round--knockout');
        }
        section.setAttribute('aria-labelledby', `offsider-laliga-round-${roundKey}`);

        const heading = document.createElement('h4');
        heading.className = 'offsider-laliga-calendar-modal__round-title';
        heading.id = `offsider-laliga-round-${roundKey}`;
        heading.textContent = formatRoundLabel(this.data, roundKey);
        section.appendChild(heading);

        const list = document.createElement('div');
        list.className = 'offsider-laliga-calendar-modal__matches';

        for (const match of roundMatches) {
          list.appendChild(this.#createMatchCard(match));
        }

        section.appendChild(list);
        fragment.appendChild(section);
      }

      body.replaceChildren(fragment);
      this.calendarBuilt = true;
    }
  }

  class LaligaCalendarModal {
    /** @param {HTMLElement} section */
    constructor(section) {
      this.section = section;
      this.dialog = section.querySelector('[data-offsider-laliga-calendar]');
      this.openBtn = section.querySelector('[data-offsider-laliga-calendar-open]');
      this.closeBtn = section.querySelector('[data-offsider-laliga-calendar-close]');
      this.title = section.querySelector('.offsider-laliga-calendar-modal__title');
      this.matchesController = null;
    }

    /** @param {LaligaMatchesController} matchesController */
    bind(matchesController) {
      this.matchesController = matchesController;

      this.openBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
    }

    open() {
      if (!(this.dialog instanceof HTMLDialogElement)) return;

      this.matchesController?.buildCalendarModal();
      this.dialog.showModal();

      requestAnimationFrame(() => {
        this.title?.focus();
      });
    }

    close() {
      if (!(this.dialog instanceof HTMLDialogElement) || !this.dialog.open) return;
      this.dialog.close();

      const openBtn = this.openBtn;
      if (openBtn instanceof HTMLElement) {
        requestAnimationFrame(() => openBtn.focus());
      }
    }
  }

  class LaligaStandingsModal {
    /** @param {HTMLElement} section */
    constructor(section) {
      this.section = section;
      this.dialog = section.querySelector('[data-offsider-laliga-standings]');
      this.openBtn = section.querySelector('[data-offsider-laliga-standings-open]');
      this.closeBtn = section.querySelector('[data-offsider-laliga-standings-close]');
      this.title = section.querySelector('.offsider-laliga-standings-modal__title');
    }

    init() {
      this.openBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
    }

    open() {
      if (!(this.dialog instanceof HTMLDialogElement)) return;

      this.dialog.showModal();

      requestAnimationFrame(() => {
        this.title?.focus();
      });
    }

    close() {
      if (!(this.dialog instanceof HTMLDialogElement) || !this.dialog.open) return;
      this.dialog.close();

      const openBtn = this.openBtn;
      if (openBtn instanceof HTMLElement) {
        requestAnimationFrame(() => openBtn.focus());
      }
    }
  }

  /** @param {HTMLElement} section */
  function initSection(section) {
    const matchesRoot = section.querySelector('[data-offsider-laliga-matches]');

    if (matchesRoot) {
      const matches = new LaligaMatchesController(matchesRoot);
      matches.init();

      const calendar = new LaligaCalendarModal(section);
      calendar.bind(matches);
    }

    if (section.querySelector('[data-offsider-laliga-standings]')) {
      const standings = new LaligaStandingsModal(section);
      standings.init();
    }
  }

  function initAll() {
    document.querySelectorAll('.offsider-laliga-dashboard').forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector?.('.offsider-laliga-dashboard');
    if (section instanceof HTMLElement) initSection(section);
  });
})();
